import React, { useState } from "react";
import { Check, Minus } from "lucide-react";
import { Seo, faqLd } from "../components/Seo";
import { PRICING_PLANS, PRICING_FEATURES, PRICING_FAQ, FINAL_CTA, FREE_TRIAL_URL } from "../mock";
import { PageHero, Reveal, SectionHeader, CheckItem, DarkCTA, FaqList, CtaLink } from "../components/Shared";

const Cell = ({ value }) => {
  if (value === true) return <Check size={18} strokeWidth={2.5} className="mx-auto text-[var(--fr-primary)]" aria-label="Included" />;
  if (value === false) return <Minus size={16} className="mx-auto text-[var(--fr-ink)]/20" aria-label="Not included" />;
  if (value === "addon")
    return <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)]">Add-on</span>;
  return <span className="text-sm font-medium">{value}</span>;
};

const Pricing = () => {
  const [yearly, setYearly] = useState(true);

  return (
    <>
      <Seo title="Pricing — Simple plans priced by tasks, unlimited riders" description="Business Starter, Standard and Growth plans priced by tasks, not seats. Unlimited riders, 14-day free trial, no card required." jsonLd={faqLd(PRICING_FAQ)} />
      <PageHero
        eyebrow="Pricing"
        title="Choose the plan that's right for your business."
        subtitle="Try it free for 14 days. No credit card required. Every plan includes the web app and the rider & dispatcher mobile apps."
      >
        <div
          className="mt-10 inline-flex items-center gap-1 p-1 rounded-full border border-[var(--fr-border)] bg-white fade-up delay-3"
          data-testid="billing-toggle"
        >
          <button
            type="button"
            onClick={() => setYearly(false)}
            className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${!yearly ? "bg-[var(--fr-ink)] text-white" : "text-[var(--fr-ink-2)]"}`}
            data-testid="billing-monthly"
          >
            Billed monthly
          </button>
          <button
            type="button"
            onClick={() => setYearly(true)}
            className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${yearly ? "bg-[var(--fr-ink)] text-white" : "text-[var(--fr-ink-2)]"}`}
            data-testid="billing-annual"
          >
            Billed yearly <span className={`ml-1 font-semibold ${yearly ? "text-white/80" : "text-[var(--fr-primary)]"}`}>save up to 20%</span>
          </button>
        </div>
      </PageHero>

      {/* Plans */}
      <section className="section pt-0" data-testid="pricing-plans">
        <div className="container-x grid md:grid-cols-3 gap-5 items-stretch">
          {PRICING_PLANS.map((p, i) => {
            const price = yearly ? p.yearly : p.monthly;
            const slug = p.name.toLowerCase().replace(/\s+/g, "-");
            return (
              <Reveal
                key={p.name}
                delay={i * 80}
                className={`${p.highlight ? "dark-block !rounded-[20px]" : "card card-hover"} p-7 flex flex-col`}
                data-testid={`plan-${slug}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display font-bold text-xl">{p.name}</h3>
                  {p.highlight && (
                    <span className="px-3 py-1 rounded-full bg-[var(--fr-primary)] text-white text-[11px] font-semibold uppercase tracking-[0.12em]">
                      Popular
                    </span>
                  )}
                </div>
                <p className={`mt-2 text-[15px] ${p.highlight ? "text-white/70" : "text-[var(--fr-ink-2)]"}`}>{p.text}</p>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-bold tracking-tight" data-testid={`plan-price-${slug}`}>
                    ${price}
                  </span>
                  <span className={`text-sm ${p.highlight ? "text-white/60" : "text-[var(--fr-muted)]"}`}>/ month</span>
                </div>
                <div className={`text-xs mt-1 ${p.highlight ? "text-white/50" : "text-[var(--fr-muted)]"}`}>
                  {yearly ? "billed yearly" : "billed monthly"}
                </div>

                <ul className={`mt-7 space-y-3 flex-1 ${p.highlight ? "text-white/90" : ""}`}>
                  <CheckItem>
                    <strong className="font-semibold">{p.deliveries}</strong> tasks per month
                  </CheckItem>
                  <CheckItem>Unlimited riders</CheckItem>
                  <CheckItem>Extra tasks at ${p.extraTask} each</CheckItem>
                  <CheckItem>Web app + rider & dispatcher apps</CheckItem>
                </ul>

                <CtaLink
                  cta={{ href: FREE_TRIAL_URL, external: true, label: "Get started" }}
                  className={`btn mt-8 w-full ${p.highlight ? "btn-primary" : "btn-outline"}`}
                  data-testid={`plan-cta-${slug}`}
                />
              </Reveal>
            );
          })}
        </div>
        <div className="container-x !mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-[var(--fr-muted)]">
          <span>Prices in USD. 14-day free trial on every plan — no card required.</span>
          <span>
            Need more than 6,000 tasks a month?{" "}
            <a href="#enterprise" className="font-semibold text-[var(--fr-primary-dark)] hover:underline" data-testid="pricing-enterprise-link">
              See Enterprise
            </a>
          </span>
        </div>
      </section>

      {/* Feature comparison */}
      <section className="section bg-[var(--fr-bg-alt)]" data-testid="pricing-features">
        <div className="container-x">
          <SectionHeader eyebrow="Compare plans" title="Key features by plan" subtitle="Everything you need to run deliveries. Add-ons can be enabled on eligible plans." />
          <Reveal className="mt-12 overflow-x-auto">
            <table className="cmp-table min-w-[680px]" data-testid="pricing-features-table">
              <thead>
                <tr>
                  <th className="eyebrow !text-[11px] w-[40%]">Feature</th>
                  {PRICING_PLANS.map((p) => (
                    <th key={p.name} className={`text-center font-semibold w-[20%] ${p.highlight ? "col-b text-[var(--fr-primary-dark)]" : ""}`}>
                      {p.name.replace("Business ", "")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PRICING_FEATURES.map((f) => (
                  <tr key={f.name}>
                    <td className="text-[var(--fr-ink-2)]">{f.name}</td>
                    <td className="text-center">
                      <Cell value={f.starter} />
                    </td>
                    <td className="text-center col-b">
                      <Cell value={f.standard} />
                    </td>
                    <td className="text-center">
                      <Cell value={f.growth} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-[1fr_2fr] gap-12">
          <SectionHeader title="Pricing questions" subtitle="The details, up front." />
          <Reveal>
            <FaqList items={PRICING_FAQ} testid="pricing-faq" />
          </Reveal>
        </div>
      </section>

      <DarkCTA
        id="enterprise"
        eyebrow="Enterprise"
        title="Running more than 6,000 tasks a month?"
        text="Enterprise plans come with volume pricing, custom SLAs, dedicated onboarding and integrations built around your ERP and e-commerce stack. Tell us about your operation and we'll design the right plan."
        cta={{ label: "Contact us for Enterprise", to: "/contact" }}
        secondary={{ label: "Start a free trial", href: FREE_TRIAL_URL, external: true }}
        email={FINAL_CTA.email}
      />
    </>
  );
};

export default Pricing;
